import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useSelector, useDispatch } from "react-redux";
import { GET_USER_BY_ID } from "@/lib/graphql/queries";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ImageUpload from "@/components/ui/image-upload";
import EditProfileModal from "@/components/modals/EditProfileModal";
import { getUserProfilePicture } from "@/services/userService";
import { updateCoverPhoto, updateProfilePhoto, updateProfile as updateProfileService } from "@/services/profileService";
import { updateUserPhoto } from "@/features/auth/authSlice";
import { useIsMobile } from "@/hooks/use-mobile";
import { FaPencilAlt, FaMapMarkerAlt } from "react-icons/fa";
import { Github, Linkedin, Twitter, Globe, ArrowLeft } from "lucide-react";

// Define the shape of the user profile data
interface ProfileType {
  id: string;
  username: string;
  email: string;
  roles: string[];
  title: string | null;
  location: string | null;
  bio: string | null;
  skills: string[] | null;
  profilePhoto: string | null;
  coverPhoto: string | null;
  linkedin: string | null;
  github: string | null;
  website: string | null;
  x: string | null;
  mobile: string | null; // Add this line to match the Profile interface

}

const UserProfile = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Get the current logged-in user from Redux store
  const currentUser = useSelector((state: any) => state.auth.user);

  // State for profile and cover images, and updating status
  const [profileImage, setProfileImage] = useState("/default-avatar.png");
  const [coverImage, setCoverImage] = useState("/default-cover.jpg");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch user data by ID using GraphQL query
  const { data: userData, loading, error, refetch } = useQuery<{ getUserById: ProfileType }>(
    GET_USER_BY_ID,
    {
      variables: { id: parseInt(id || "0", 10) },
      fetchPolicy: "network-only",
      onError: (error) => {
        console.error("GraphQL error:", error);
        toast({
          title: "Error",
          description: "Failed to load user profile",
          variant: "destructive",
        });
      },
    }
  );

  // Load profile and cover images when user data is available
  useEffect(() => {
    if (userData?.getUserById) {
      const user = userData.getUserById;
      if (user.profilePhoto) {
        (async () => {
          try {
            const result = await getUserProfilePicture(user.profilePhoto);
            const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
            setProfileImage(imageUrl);
          } catch (error) {
            console.error("Error loading profile picture:", error);
          }
        })();
      }
      
      // Fix: Properly load cover photo using getUserProfilePicture
      if (user.coverPhoto) {
        (async () => {
          try {
            const result = await getUserProfilePicture(user.coverPhoto);
            const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
            setCoverImage(imageUrl);
          } catch (error) {
            console.error("Error loading cover photo:", error);
          }
        })();
      }
    }
  }, [userData]);

  // Handler for updating cover photo (only for own profile)
  const handleCoverPhotoChange = async (file: File) => {
    // Add an early return if the current user is not the profile owner
    if (!currentUser || currentUser.id !== id) {
      toast({
        title: "Permission Denied",
        description: "You can only update your own profile",
        variant: "destructive",
      });
      return;
    }
    
    setIsUpdating(true);
    try {
      const updatedProfile = await updateCoverPhoto(file);
      if (updatedProfile.coverPhoto) {
        setCoverImage(updatedProfile.coverPhoto);
        toast({
          title: "Success",
          description: "Cover photo updated successfully",
        });
      }
    } catch (error) {
      console.error("Error updating cover photo:", error);
      toast({
        title: "Error",
        description: "Failed to update cover photo",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for updating profile photo (only for own profile)
  const handleProfilePhotoChange = async (file: File) => {
    if (!currentUser || currentUser.id !== id) return;
    setIsUpdating(true);
    try {
      const updatedProfile = await updateProfilePhoto(file);
      if (updatedProfile.profilePhoto) {
        const result = await getUserProfilePicture(updatedProfile.profilePhoto);
        const imageUrl = `data:${result.mimetype};base64,${result.base64Data}`;
        setProfileImage(imageUrl);
        dispatch(updateUserPhoto(updatedProfile.profilePhoto));
        toast({
          title: "Success",
          description: "Profile photo updated successfully",
        });
      }
    } catch (error) {
      console.error("Error updating profile photo:", error);
      toast({
        title: "Error",
        description: "Failed to update profile photo",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for updating profile details (only for own profile)
  const handleProfileUpdate = async (updatedData: Partial<ProfileType>) => {
    if (!currentUser || currentUser.id !== id) return;
    setIsUpdating(true);
    try {
      await updateProfileService(updatedData);
      await refetch();
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-xl">Loading profile...</div>
      </div>
    );
  }

  // Error or user not found state
  if (error || !userData?.getUserById) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 p-4 rounded-md text-red-600 mb-4">
          {error ? `Error loading profile: ${error.message}` : "User not found"}
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const user = userData.getUserById;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Back Button */}
      <div className="container mx-auto px-4 py-4">
        <Button onClick={() => navigate(-1)} variant="outline" size="sm">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>

      {/* Cover Photo Section */}
      <div className="relative">
        {currentUser?.id === user.id ? (
          <ImageUpload
            currentImage={coverImage}
            onImageChange={handleCoverPhotoChange}
            className="h-[200px] sm:h-[300px] w-full object-cover"
          />
        ) : (
          <div className="h-[200px] sm:h-[300px] w-full">
            <img 
              src={coverImage} 
              alt="Cover" 
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-[100px]">
        <div className="relative bg-background rounded-lg shadow-lg p-4 sm:p-6">
          <div className="relative">
            <div className="mx-auto sm:mx-0 sm:absolute -top-16 left-6 z-10">
              {currentUser?.id === user.id ? (
                <ImageUpload
                  currentImage={profileImage}
                  onImageChange={handleProfilePhotoChange}
                  isProfile={true}
                  className="h-32 w-32 rounded-full"
                />
              ) : (
                <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
                  <AvatarImage src={profileImage} alt={user.username || "User"} />
                  <AvatarFallback>
                    {user.username?.substring(0, 2).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>

            <div className="mt-16 sm:mt-0 sm:ml-[160px] text-center sm:text-left">
              <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-4">
                <div>
                  <h1 className="text-2xl font-bold">{user.username || "Anonymous User"}</h1>
                  <p className="text-lg text-muted-foreground">{user.title}</p>
                  {user.location && (
                    <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-sm text-muted-foreground">
                      <FaMapMarkerAlt className="h-4 w-4" />
                      <span>{user.location}</span>
                    </div>
                  )}
                </div>
                {currentUser?.id === user.id && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 w-full sm:w-auto mt-4 sm:mt-0"
                    onClick={() => setIsEditModalOpen(true)}
                  >
                    <FaPencilAlt className="h-4 w-4" />
                    Edit Profile
                  </Button>
                )}
              </div>

              <div className="flex gap-4 mt-4 justify-center sm:justify-start">
                {user.linkedin && (
                  <a
                    href={`https://linkedin.com/in/${user.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition"
                  >
                    <Linkedin className="h-6 w-6" />
                  </a>
                )}
                {user.github && (
                  <a
                    href={`https://github.com/${user.github}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition"
                  >
                    <Github className="h-6 w-6" />
                  </a>
                )}
                {user.x && (
                  <a
                    href={`https://x.com/${user.x}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition"
                  >
                    <Twitter className="h-6 w-6" />
                  </a>
                )}
                {user.website && (
                  <a
                    href={user.website.startsWith("http") ? user.website : `https://${user.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition"
                  >
                    <Globe className="h-6 w-6" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-6 grid grid-cols-1 gap-6">
          {/* Skills Section - Top on Mobile */}
          <div className="block md:hidden">
            <Card>
              <CardHeader>
                <CardTitle>Skills</CardTitle>
              </CardHeader>
              <CardContent>
                {user.skills && user.skills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No skills listed</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>About</CardTitle>
                </CardHeader>
                <CardContent>
                  {user.bio ? (
                    <p className="text-muted-foreground">{user.bio}</p>
                  ) : (
                    <p className="text-muted-foreground">No bio available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="hidden md:block space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Skills</CardTitle>
                </CardHeader>
                <CardContent>
                  {user.skills && user.skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {user.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No skills listed</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Edit Button on Mobile (only for own profile) */}
      {isMobile && currentUser?.id === user.id && (
        <button
          onClick={() => setIsEditModalOpen(true)}
          className="fixed bottom-6 right-6 bg-primary text-primary-foreground rounded-full p-4 shadow-lg hover:bg-primary/90 transition-colors"
        >
          <FaPencilAlt className="w-5 h-5" />
        </button>
      )}

      {/* Edit Profile Modal (only for own profile) */}
      {currentUser?.id === user.id && (
        <EditProfileModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          profileData={user}
          onSave={handleProfileUpdate}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default UserProfile;